/* eslint-disable max-lines */
import { AxiosResponse } from 'axios';
import { apiAuthCaller } from '@services/apiCaller';
import { getApiErrorMessage } from '@components/common/util/util';
import { ServerSource } from '@stores/sourcesList/types';
import { ServerDestination } from '@stores/destinationsList';
import { ServerTransformation } from '@stores/transformationsList';
import { ITrackingPlan } from '@stores/trackingPlans/types';
import { WhtProjectServerResponse } from '@components/profiles/stores/types';
import { Region } from '@components/common/constants';
import {
  ApiGroup,
  ApiPolicy,
  ApiUser,
  ApiInvitation,
  ApiResponse,
  ApiPermissionDefinition,
  UpdateGroupApiPayload,
  UpdatePolicyApiPayload,
  CreatePolicyApiPayload,
  CreateGroupApiPayload,
  CreateGroupApiResponse,
  DeletePolicyApiPayload,
  ModifyMembershipsApiPayload,
  DeleteUsersApiPayload,
  UpdateUserRoleApiPayload,
  ApiAccountDefinition,
  CreateUserInvitationsApiPayload,
  OauthFlowPayload,
  InitiateOauthFlowApiResponse,
  CompleteOauthFlowApiResponse,
  CreateAccountPayload,
  CreateAccountResponse,
  UpdateAccountPayload,
  ApiAccount,
  ApiLegacyAccount,
  ApiServiceAccessToken,
  CreateServiceAccessTokenApiPayload,
  CreateServiceAccessTokenApiResponse,
  AddEventsToBlockListResponse,
  AddEventsToBlockListRequest,
  BlockListStatusRequest,
  BlockListResponse,
  SourceBlockedEventsResponse,
  SourceBlockedEventsParams,
  BlockingListByResourceResponse,
  SnowpipeMigrationStatusResponse,
  SnowpipeMigrationStartResponse,
  ApiEventUsageStatus,
  Timestamp,
  SelfServeMigrationActionApiPayload,
  ApiTransformationsLibrary,
  BaseCodeEntityCreatePayload,
  CodeLanguageType,
  ApiTransformation,
  ApiTransformationTemplate,
  TransformationConnectionConfig,
  TransformationLiveEvent,
  TransformationTestResult,
  TransformationCreatePayload,
  FacebookPagesResponse,
  FacebookPageResponse,
  CodeEntityRelease,
  ApiResponseWithPagination,
  PaginationParams,
  ApiErrorResponse,
  ApiTestEventPayload,
  TestEventPayloadCreateInput,
  TestEventPayloadUpdateInput,
  ApiTransformationTest,
  TestDefinition,
  ApiTransformationTestSuiteRunResult,
  ApiBaseCodeEntity,
  ApiDataGraph,
  DGListResponse,
  DGListParams,
  CreateDataGraphPayload,
  ApiModel,
  CreateModelPayload,
  UpdateModelPayload,
  ValidateModelPayload,
  ValidateModelResponse,
  ApiRelationship,
  CreateRelationshipPayload,
  UpdateRelationshipPayload,
  ValidateRelationshipPayload,
  ValidateRelationshipResponse,
  ModelTableSchemaResponse,
  WarehouseDatabasesPayload,
  WarehouseDatabasesResponse,
  WarehouseSchemasPayload,
  WarehouseSchemasResponse,
  WarehouseTablesPayload,
  WarehouseTablesResponse,
  DataGraphAudienceSourceResponse,
  CreateDataGraphAudienceSourcePayload,
  UpdateDataGraphAudienceSourcePayload,
  WorkspaceTokenCreateRequest,
  WorkspaceTokenCreateResponse,
  WorkspaceTokenListItem,
  EnrichedProperty,
  ExpressionContext,
} from './types';
import { BlockListResourceType, EVENT_BLOCKING_API_URLS, AMMigrationState } from './constants';

export const getResourceCacheKey = (
  sessionId: number,
  workspaceId: string,
  resource: ApiGetResource,
) => `${sessionId}-${workspaceId}-${resource}`;

export const getOrganizationPath = (organizationId: string) => `organizations/${organizationId}`;

export const getWorkspacePath = (workspaceId: string) => `workspaces/${workspaceId}`;

export enum ApiGetResource {
  WORKSPACE_POLICIES = 'workspace-policies',
  GROUPS = 'groups',
  USERS = 'users',
  PERMISSION_DEFINITIONS = 'permissions',
  USER_INVITATIONS = 'user-invitations',
  SERVICE_ACCESS_TOKENS = 'service-access-tokens',
  MIGRATION_STATE = 'self-serve-migrations/access-management/state',
  ACCOUNT_DEFINITIONS = 'account-definitions',
  ACCOUNTS = 'accounts',
  ACCOUNTS_LEGACY = 'accounts_legacy',

  SOURCES = 'sources',
  DESTINATIONS = 'destinations',
  TRANSFORMATIONS = 'transformations',
  TRACKING_PLANS = 'trackingPlans',
  PROFILES = 'whtProjects',
}

type GetResourceResponse = {
  [ApiGetResource.WORKSPACE_POLICIES]: ApiResponse<ApiPolicy[]>;
  [ApiGetResource.GROUPS]: ApiResponse<ApiGroup[]>;
  [ApiGetResource.USERS]: ApiResponse<ApiUser[]>;
  [ApiGetResource.PERMISSION_DEFINITIONS]: ApiResponse<ApiPermissionDefinition[]>;
  [ApiGetResource.USER_INVITATIONS]: ApiResponse<ApiInvitation[]>;
  [ApiGetResource.SERVICE_ACCESS_TOKENS]: ApiServiceAccessToken[];
  [ApiGetResource.MIGRATION_STATE]: ApiResponse<{ state: AMMigrationState }>;
  [ApiGetResource.ACCOUNT_DEFINITIONS]: ApiResponse<ApiAccountDefinition[]>;
  [ApiGetResource.ACCOUNTS]: ApiResponse<ApiAccount[]>;
  [ApiGetResource.ACCOUNTS_LEGACY]: ApiResponse<ApiLegacyAccount[]>;

  [ApiGetResource.SOURCES]: { sources: ServerSource[] };
  [ApiGetResource.DESTINATIONS]: { destinations: ServerDestination[] };
  [ApiGetResource.TRANSFORMATIONS]: { transformations: ServerTransformation[] };
  [ApiGetResource.TRACKING_PLANS]: ITrackingPlan[];
  [ApiGetResource.PROFILES]: WhtProjectServerResponse[];
};

type GetResourceParams<R extends ApiGetResource> = {
  path: string;
  resource?: R;
  params?: Record<string, string | number | Array<string> | Array<number>>;
  errorMessage?: string;
  onSuccess?: (data: GetResourceResponse[R]) => void;
  onError?: (error: string) => void;
  region?: Region;
};

export const fetchData =
  <R extends ApiGetResource>({
    path,
    resource,
    params,
    errorMessage,
    onSuccess,
    onError,
    region,
  }: GetResourceParams<R>): (() => Promise<GetResourceResponse[R]>) =>
  async () => {
    try {
      const resp = await apiAuthCaller({ nonWorkspaceRoute: true, region }).get<
        GetResourceResponse[R]
      >(`${path}${resource ? `/${resource}` : ''}`, { params });
      onSuccess?.(resp.data);
      return resp.data;
    } catch (e) {
      const error = getApiErrorMessage(e, errorMessage || '');
      onError?.(error);
      throw error;
    }
  };

export const updatePolicyApi = (organizationId: string, payload: UpdatePolicyApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .put(`${getOrganizationPath(organizationId)}/workspace-policies`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update policy');
    });

export const updateGroupApi = (
  organizationId: string,
  groupId: string,
  payload: UpdateGroupApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .put(`${getOrganizationPath(organizationId)}/groups/${groupId}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update group');
    });

export const createGroupApi = (organizationId: string, payload: CreateGroupApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post<CreateGroupApiResponse>(`${getOrganizationPath(organizationId)}/groups`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create group');
    });

export const createPolicyApi = (organizationId: string, payload: CreatePolicyApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post(`${getOrganizationPath(organizationId)}/workspace-policies`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create policy');
    });

export const deletePolicyApi = (organizationId: string, payload: DeletePolicyApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete<DeletePolicyApiPayload>(`${getOrganizationPath(organizationId)}/workspace-policies`, {
      data: payload,
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete policy');
    });

export const deleteGroupApi = (organizationId: string, groupId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete(`${getOrganizationPath(organizationId)}/groups/${groupId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete group');
    });

export const addMembershipsApi = (organizationId: string, payload: ModifyMembershipsApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post(`${getOrganizationPath(organizationId)}/memberships`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to add memberships');
    });

export const removeMembershipsApi = (
  organizationId: string,
  payload: ModifyMembershipsApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete(`${getOrganizationPath(organizationId)}/memberships`, { data: payload })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to remove memberships');
    });

export const deleteUsersApi = (organizationId: string, payload: DeleteUsersApiPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete(`${getOrganizationPath(organizationId)}/users`, { data: payload })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete users');
    });

export const updateUserRoleApi = (
  organizationId: string,
  userId: string,
  payload: UpdateUserRoleApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .patch(`${getOrganizationPath(organizationId)}/users/${userId}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update user role');
    });

export const createUserInvitationsApi = (
  organizationId: string,
  payload: CreateUserInvitationsApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US })
    .post(`${getOrganizationPath(organizationId)}/user-invitations`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to send user invitation');
    });

export const deleteUserInvitationApi = (organizationId: string, invitationId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US })
    .delete(`${getOrganizationPath(organizationId)}/user-invitations/${invitationId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete user invitation');
    });

export const resendUserInvitationApi = (organizationId: string, invitationId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US })
    .put(`${getOrganizationPath(organizationId)}/user-invitations/${invitationId}/resend`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to resend user invitation');
    });

export const createServiceAccessTokenApi = (
  organizationId: string,
  payload: CreateServiceAccessTokenApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post<CreateServiceAccessTokenApiResponse>(
      `${getOrganizationPath(organizationId)}/service-access-tokens`,
      payload,
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create service access token');
    });

export const deleteServiceAccessTokenApi = (organizationId: string, tokenId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete(`${getOrganizationPath(organizationId)}/service-access-tokens/${tokenId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete service access token');
    });

export const selfServeMigrationActionApi = (
  organizationId: string,
  payload: SelfServeMigrationActionApiPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post(
      `${getOrganizationPath(organizationId)}/self-serve-migrations/access-management/action`,
      payload,
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to perform migration action');
    });

export const initiateOauthFlowApi = (payload: OauthFlowPayload) =>
  apiAuthCaller()
    .post<InitiateOauthFlowApiResponse>('oauth/url', payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to initiate oauth');
    });

export const completeOauthFlowApi = (payload: OauthFlowPayload, query: string) =>
  apiAuthCaller()
    .post<CompleteOauthFlowApiResponse>(`oauth/callback${query}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to complete oauth');
    });

export const deleteAccountApi = (workspaceId: string, accountId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .delete(`/web/workspaces/${workspaceId}/accounts/${accountId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete account');
    });

export const createNonOauthAccountApi = (workspaceId: string, payload: CreateAccountPayload) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .post<CreateAccountResponse>(`/web/workspaces/${workspaceId}/accounts`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create account');
    });

export const updateNonOauthAccountApi = (
  workspaceId: string,
  accountId: string,
  payload: UpdateAccountPayload,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .patch<CreateAccountResponse>(`/web/workspaces/${workspaceId}/accounts/${accountId}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update account');
    });

export const addEventsToWorkspaceBlockList = (eventIds: string[]) =>
  apiAuthCaller()
    .post<AddEventsToBlockListResponse>(EVENT_BLOCKING_API_URLS.workspaceBlockListEvents, {
      eventIds,
    } as AddEventsToBlockListRequest)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to block the provided events');
    });

export const changeWorkspaceBlockListEnabledStatus = (enabledStatus: boolean) =>
  apiAuthCaller()
    .patch(EVENT_BLOCKING_API_URLS.workspaceBlockList, {
      enabled: enabledStatus,
    } as BlockListStatusRequest)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update workspace block list status');
    });

export const getWorkspaceBlockList = () =>
  apiAuthCaller()
    .get<BlockListResponse>(EVENT_BLOCKING_API_URLS.workspaceBlockList)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to load workspace block list');
    });

export const removeEventsFromWorkspaceBlockList = (eventIds: string[]) =>
  apiAuthCaller()
    .delete<AddEventsToBlockListResponse>(EVENT_BLOCKING_API_URLS.workspaceBlockListEvents, {
      params: {
        eventIds: eventIds.join(','),
      },
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to remove provided event from block list');
    });

export const getSourceBlockedEvents = (sourceId: string, params: SourceBlockedEventsParams) =>
  apiAuthCaller()
    .get<SourceBlockedEventsResponse>(EVENT_BLOCKING_API_URLS.sourceBlockedEvents(sourceId), {
      params,
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get source blocked events');
    });

export const getBlockingListByResource = (
  resourceType: BlockListResourceType,
  resourceId: string,
) =>
  apiAuthCaller()
    .get<BlockingListByResourceResponse>(EVENT_BLOCKING_API_URLS.blockLists, {
      params: {
        resourceType,
        resourceId,
      },
    })
    .catch((e) => {
      throw getApiErrorMessage(e, `Failed to load ${resourceType} block list`);
    });

export const getSnowpipeMigrationCompletedSteps = (destinationId: string) =>
  apiAuthCaller()
    .get<SnowpipeMigrationStatusResponse>(`/destinations/${destinationId}/snowpipeMigration/status`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch migration status');
    });

export const startSnowpipeMigration = (destinationId: string) =>
  apiAuthCaller()
    .post<SnowpipeMigrationStartResponse>(`/destinations/${destinationId}/snowpipeMigration/start`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to start migration');
    });

export const endSnowpipeMigration = (destinationId: string) =>
  apiAuthCaller()
    .post<unknown>(`/destinations/${destinationId}/snowpipeMigration/end`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to finish migration');
    });

export const rollbackSnowpipeMigration = (destinationId: string) =>
  apiAuthCaller()
    .post<unknown>(`/destinations/${destinationId}/snowpipeMigration/rollback`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to rollback migration');
    });

export const getEventUsageStatus = (organizationId: string) =>
  apiAuthCaller({ nonWorkspaceRoute: true })
    .get<ApiResponse<ApiEventUsageStatus>>(`/organizations/${organizationId}/event-usage-status`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get event usage status');
    });

export const getEventsCountsBySourceDefinitionCategory = <T>(
  organizationId: string,
  params: {
    workspaceIds: string;
    start: Timestamp;
  },
  region: Region,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true, region })
    .get<T>(`/organizations/${organizationId}/eventsCountBySourceCategory`, { params })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get events counts');
    });

export const getEventsCountsBySourceAndTimeBucket = <T>(
  organizationId: string,
  params: {
    workspaceIds: string;
    granularity: string;
    start: Timestamp;
  },
  region: Region,
) =>
  apiAuthCaller({ nonWorkspaceRoute: true, region })
    .get<T>(`/organizations/${organizationId}/eventsCountBySourceAndTimeBucket`, { params })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get events counts');
    });

export const patchTransformationLibrary = (
  transformationLibraryId: string,
  payload: {
    description?: string;
  },
) =>
  apiAuthCaller().patch<ApiTransformationsLibrary>(
    `/transformationLibraries/${transformationLibraryId}`,
    payload,
  );

export const saveTransformationLibraryDraftCode = (
  transformationLibraryId: string,
  code: string,
  options?: {
    publish?: boolean;
    tag?: string;
    comment?: string;
    testEvents?: Record<string, unknown>[];
    skipValidation?: boolean;
  },
) =>
  apiAuthCaller().post<ApiTransformationsLibrary>(
    `/transformationLibraries/${transformationLibraryId}/draft`,
    { code, testEvents: options?.testEvents, tag: options?.tag, comment: options?.comment },
    {
      params: {
        publish: options?.publish ?? false,
        skipValidation: options?.skipValidation ?? false,
      },
    },
  );

export const getTransformationLibraryById = (transformationLibraryId: string) =>
  apiAuthCaller()
    .get<ApiTransformationsLibrary>(`/transformationLibraries/${transformationLibraryId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch transformation library');
    });

export const getAllTransformationLibraries = () =>
  apiAuthCaller()
    .get<{ libraries: ApiTransformationsLibrary[] }>(`/transformationLibraries`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch transformation libraries ');
    });

export const createTransformationLibrary = (
  payload: BaseCodeEntityCreatePayload,
  options?: { publish: boolean },
) =>
  apiAuthCaller().post<ApiTransformationsLibrary>(`/transformationLibraries`, payload, {
    params: { publish: options?.publish ?? false },
  });

export const deleteTransformationLibrary = (transformationLibraryId: string) =>
  apiAuthCaller()
    .delete<void>(`/transformationLibraries/${transformationLibraryId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete transformation library');
    });

export const getStandardTransformationLibraries = () =>
  apiAuthCaller()
    .get<{
      rudderstackLibraries: Pick<
        ApiTransformationsLibrary,
        'id' | 'name' | 'importName' | 'code' | 'language'
      >[];
    }>(`/rudderstackTransformationLibraries`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch standard transformation libraries');
    });

export const testTransformationLibraryCode = (input: {
  code: string;
  language: CodeLanguageType;
}) => apiAuthCaller().ignore400().post<void>(`/transformationLibrary/test`, input);

export const saveTransformationDraftCode = (
  transformationId: string,
  code: string,
  options?: {
    publish: boolean;
    events?: string;
    tag?: string;
    comment?: string;
    skipValidation?: boolean;
  },
) =>
  apiAuthCaller().post<ApiTransformation>(
    `/transformations/${transformationId}/draft`,
    { code, testEvents: options?.events, tag: options?.tag, comment: options?.comment },
    {
      params: {
        publish: options?.publish ?? false,
        skipValidation: options?.skipValidation ?? false,
      },
    },
  );

export const patchTransformation = (
  transformationId: string,
  input?: { name?: string; description?: string },
) => apiAuthCaller().patch<ApiTransformation>(`/transformations/${transformationId}`, input);

export const createTransformation = (
  input: TransformationCreatePayload,
  options?: { publish: boolean },
) =>
  apiAuthCaller().post<ApiTransformation>('/transformations', input, {
    params: { publish: options?.publish ?? false },
  });

export const getAllTransformations = () =>
  apiAuthCaller()
    .get<{ transformations: ApiTransformation[] }>('/transformations')
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch transformations');
    });

export const deleteTransformation = (transformationId: string) =>
  apiAuthCaller()
    .delete<void>(`/transformations/${transformationId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete transformation');
    });

export const getTransformationTemplates = () =>
  apiAuthCaller()
    .get<{ rudderstackTransformations: ApiTransformationTemplate[] }>('/rudderstackTransformations')
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch transformation templates');
    });

export const getTransformationById = (transformationId: string) =>
  apiAuthCaller()
    .get<ApiTransformation>(`/transformations/${transformationId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch transformation');
    });

export const testTransformationCode = ({
  events,
  ...input
}: {
  code: string;
  language: CodeLanguageType;
  events?: string;
  id: string;
}) =>
  apiAuthCaller()
    .ignore400()
    .post<TransformationTestResult>(`/transformation/test`, {
      ...input,
      input: events,
      codeVersion: '1',
    });

export const connectTransformationToDestination = (
  transformationId: string,
  destinationId: string,
  config?: TransformationConnectionConfig,
) =>
  apiAuthCaller()
    .post<void>(`/transformations/${transformationId}/connectToDestination`, {
      destinationId,
      config,
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to connect transformation to destination');
    });

export const disconnectTransformationFromDestination = (
  transformationId: string,
  destinationId: string,
) =>
  apiAuthCaller()
    .post<void>(`/transformations/${transformationId}/disconnectFromDestination`, { destinationId })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to disconnect transformation from destination');
    });

export const updateTransformationConnectionSettings = (
  transformationId: string,
  destinationId: string,
  config: TransformationConnectionConfig,
) =>
  apiAuthCaller()
    .post<void>(`/transformations/${transformationId}/updateDestinationConnectionConfig`, {
      destinationId,
      config,
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update transformation connection settings');
    });

export const getTransformationLiveEvents = (
  transformationId: string,
  options: { latestId: number; from: number },
) =>
  apiAuthCaller()
    .get<TransformationLiveEvent[]>(`/eventTransformStatus`, {
      headers: { 'X-TRANSFORMATION-ID': transformationId },
      params: { id: options.latestId, from: options.from },
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get transformation live events');
    });

export const getTransformationReleases = (
  transformationId: string,
  paginationParams: PaginationParams,
  filters?: { search?: string },
) =>
  apiAuthCaller()
    .get<ApiResponseWithPagination<CodeEntityRelease>>(
      `/transformations/${transformationId}/releases`,
      {
        params: { ...paginationParams, ...(filters ?? {}) },
      },
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get transformation releases');
    });

export const publishTransformationRelease = (
  transformationId: string,
  releaseId: string,
  options?: {
    skipValidation?: boolean;
  },
) =>
  apiAuthCaller().post<ApiTransformation>(
    `/transformations/${transformationId}/releases/${releaseId}/publish`,
    {},
    {
      params: {
        skipValidation: options?.skipValidation ?? false,
      },
    },
  );

export const getTransformationLibraryReleases = (
  transformationLibraryId: string,
  paginationParams: PaginationParams,
  filters?: { search?: string },
) =>
  apiAuthCaller()
    .get<ApiResponseWithPagination<CodeEntityRelease>>(
      `/transformationLibraries/${transformationLibraryId}/releases`,
      {
        params: { ...paginationParams, ...(filters ?? {}) },
      },
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get transformation library releases');
    });

export const getTransformationLibraryConnectedTransformations = (transformationLibraryId: string) =>
  apiAuthCaller()
    .get<ApiBaseCodeEntity[]>(`/transformationLibraries/${transformationLibraryId}/transformations`)
    .catch((e) => {
      throw new Error(
        getApiErrorMessage(e, 'Failed to get transformation library connected transformations'),
      );
    });

const isApiError = <T extends object>(response: T): response is T & ApiErrorResponse =>
  'success' in response && !response.success;

const handleApiError = async <T extends object>(
  request: Promise<AxiosResponse<T | ApiErrorResponse>>,
  errorMessageFallback: string,
) => {
  const { data } = await request.catch((e) => {
    throw getApiErrorMessage(e, errorMessageFallback);
  });

  if (isApiError(data)) {
    const { message, reason } = data.errorDetails;
    throw `${message} - ${reason}`;
  }

  return data;
};

export const listFacebookPages = ({
  sourceId,
  accountId,
  nextPageCursor,
}: {
  sourceId?: string;
  accountId: string;
  nextPageCursor?: string | null;
}) =>
  handleApiError(
    apiAuthCaller().post<FacebookPagesResponse>(
      `/integrationsInfo/v1/facebook_lead_ads_native/listPages`,
      {
        sourceId,
        rudderAccountId: accountId,
        pagination: nextPageCursor ? { next: nextPageCursor } : undefined,
      },
    ),
    'Failed to load Facebook pages',
  );

export const getFacebookPageInfo = (payload: {
  config: { pageId: string };
  sourceId: string;
  rudderAccountId: string;
}) =>
  handleApiError(
    apiAuthCaller().post<FacebookPageResponse>(
      '/integrationsInfo/v1/facebook_lead_ads_native/pageInfo',
      payload,
    ),
    'Failed to fetch Facebook page',
  );

export const syncWarehouseDestinationSchema = (destinationId: string) =>
  apiAuthCaller()
    .post(`/warehouse/destinations/${destinationId}/syncSchema`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to sync schema');
    });

export const getTestEventsPayload = () =>
  apiAuthCaller()
    .get<{ testEvents: ApiTestEventPayload[] }>('/testEvents')
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to get test events payload'));
    });

export const createTestEventsPayload = (payload: TestEventPayloadCreateInput) =>
  apiAuthCaller()
    .post<ApiTestEventPayload>('/testEvents', payload)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to save test payload'));
    });

export const updateTestEventsPayload = (id: string, payload: TestEventPayloadUpdateInput) =>
  apiAuthCaller()
    .post<ApiTestEventPayload>(`/testEvents/${id}`, payload)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to save test payload'));
    });

export const deleteTestEventsPayload = (id: string) =>
  apiAuthCaller()
    .delete<void>(`/testEvents/${id}`)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to delete test payload'));
    });

export const getTransformationTests = (transformationId: string) =>
  apiAuthCaller()
    .get<ApiTransformationTest[]>(`/transformations/${transformationId}/tests`)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to get tests'));
    });

export const getTransformationTestDetails = (testId: string) =>
  apiAuthCaller()
    .get<ApiTransformationTest>(`/transformations/tests/${testId}`)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to get test details'));
    });

export const createTransformationTest = (transformationId: string, testInput: TestDefinition) =>
  apiAuthCaller()
    .post<ApiTransformationTest>(`/transformations/${transformationId}/tests`, testInput)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to create test'));
    });

export const updateTransformationTest = (
  transformationId: string,
  testId: string,
  testInput: Partial<TestDefinition>,
) =>
  apiAuthCaller()
    .patch<ApiTransformationTest>(`/transformations/${transformationId}/tests/${testId}`, testInput)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to update test'));
    });

export const deleteTransformationTest = (transformationId: string, testId: string) =>
  apiAuthCaller()
    .delete<void>(`/transformations/${transformationId}/tests/${testId}`)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to delete test'));
    });

export const runTestSuite = (
  transformationInput: {
    code: string;
    language: CodeLanguageType;
    overrideLibraries?: { versionId: string }[];
  },
  testSuite: TestDefinition[],
) =>
  apiAuthCaller()
    .post<ApiTransformationTestSuiteRunResult>(`/transformations/tests/run`, {
      ...transformationInput,
      testSuite,
    })
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to run test suite'));
    });

export const runTransformationTestSuite = (
  id: string,
  options: {
    code?: string;
    testIds?: string[];
    overrideLibraries?: { versionId: string }[];
  },
) =>
  apiAuthCaller()
    .post<ApiTransformationTestSuiteRunResult>(`/transformations/${id}/tests/run`, options)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to run transformation test suite'));
    });

export const runTransformationLibraryImpactTestSuite = (
  { transformationId, libraryId }: { transformationId: string; libraryId: string },
  options: {
    code?: string;
    versionId?: string;
    testIds?: string[];
  },
) =>
  apiAuthCaller()
    .post<ApiTransformationTestSuiteRunResult>(
      `/transformationLibraries/${libraryId}/transformations/${transformationId}/tests/run`,
      options,
    )
    .catch((e) => {
      throw new Error(
        getApiErrorMessage(e, 'Failed to run transformation library impact test suite'),
      );
    });

export const runTransformationReleaseTestSuite = (
  transformationId: string,
  releaseId: string,
  options?: {
    testIds?: string[];
  },
) =>
  apiAuthCaller()
    .post<ApiTransformationTestSuiteRunResult>(
      `/transformations/${transformationId}/releases/${releaseId}/tests/run`,
      options,
    )
    .catch((e) => {
      throw new Error(
        getApiErrorMessage(e, 'Failed to run transformation test suite for the specified version'),
      );
    });
// --- Data Graph APIs ---

export const createDataGraph = (payload: CreateDataGraphPayload) =>
  apiAuthCaller()
    .post<ApiDataGraph>('/data-graphs', payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create data graph');
    });

export const listDataGraphs = (params?: DGListParams) =>
  apiAuthCaller()
    .get<DGListResponse<ApiDataGraph>>(`/data-graphs`, { params })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to list data graphs');
    });

export const deleteDataGraph = (dataGraphId: string) =>
  apiAuthCaller()
    .delete(`/data-graphs/${dataGraphId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete data graph');
    });

export const listModels = (dataGraphId: string, params?: DGListParams) =>
  apiAuthCaller()
    .get<DGListResponse<ApiModel>>(`/data-graphs/${dataGraphId}/models`, { params })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to list models');
    });

export const createModel = (dataGraphId: string, payload: CreateModelPayload) =>
  apiAuthCaller()
    .post<ApiModel>(`/data-graphs/${dataGraphId}/models`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create model');
    });

export const updateModel = (dataGraphId: string, modelId: string, payload: UpdateModelPayload) =>
  apiAuthCaller()
    .put<ApiModel>(`/data-graphs/${dataGraphId}/models/${modelId}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update model');
    });

export const deleteModel = (dataGraphId: string, modelId: string) =>
  apiAuthCaller()
    .delete(`/data-graphs/${dataGraphId}/models/${modelId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete model');
    });

export const validateModel = (dataGraphId: string, payload: ValidateModelPayload) =>
  apiAuthCaller()
    .post<ValidateModelResponse>(`/data-graphs/${dataGraphId}/models/validate`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to validate model');
    });

export const validateExistingModel = (dataGraphId: string, modelId: string) =>
  apiAuthCaller()
    .post<ValidateModelResponse>(`/data-graphs/${dataGraphId}/models/${modelId}/validate`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to validate model');
    });

export const listRelationships = (dataGraphId: string, params?: DGListParams) =>
  apiAuthCaller()
    .get<DGListResponse<ApiRelationship>>(`/data-graphs/${dataGraphId}/relationships`, { params })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to list relationships');
    });

export const createRelationship = (dataGraphId: string, payload: CreateRelationshipPayload) =>
  apiAuthCaller()
    .post<ApiRelationship>(`/data-graphs/${dataGraphId}/relationships`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create relationship');
    });

export const updateRelationship = (
  dataGraphId: string,
  relationshipId: string,
  payload: UpdateRelationshipPayload,
) =>
  apiAuthCaller()
    .put<ApiRelationship>(`/data-graphs/${dataGraphId}/relationships/${relationshipId}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update relationship');
    });

export const deleteRelationship = (dataGraphId: string, relationshipId: string) =>
  apiAuthCaller()
    .delete(`/data-graphs/${dataGraphId}/relationships/${relationshipId}`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to delete relationship');
    });

export const validateRelationship = (dataGraphId: string, payload: ValidateRelationshipPayload) =>
  apiAuthCaller()
    .post<ValidateRelationshipResponse>(
      `/data-graphs/${dataGraphId}/relationships/validate`,
      payload,
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to validate relationship');
    });

export const validateExistingRelationship = (dataGraphId: string, relationshipId: string) =>
  apiAuthCaller()
    .post<ValidateRelationshipResponse>(
      `/data-graphs/${dataGraphId}/relationships/${relationshipId}/validate`,
    )
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to validate relationship');
    });

export const getModelTableSchema = (dataGraphId: string, modelId: string) =>
  apiAuthCaller()
    .get<ModelTableSchemaResponse>(`/data-graphs/${dataGraphId}/models/${modelId}/schema`)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to get model schema');
    });

export const getWarehouseDatabases = (role: string, payload: WarehouseDatabasesPayload) =>
  handleApiError(
    apiAuthCaller().post<WarehouseDatabasesResponse>(
      `/cloudSources/sources/info/${role}/catalogs`,
      payload,
    ),
    'Failed to fetch databases',
  );

export const getWarehouseSchemas = (role: string, payload: WarehouseSchemasPayload) =>
  handleApiError(
    apiAuthCaller().post<WarehouseSchemasResponse>(
      `/cloudSources/sources/info/${role}/schemas`,
      payload,
    ),
    'Failed to fetch schemas',
  );

export const getWarehouseTables = (role: string, payload: WarehouseTablesPayload) =>
  handleApiError(
    apiAuthCaller().post<WarehouseTablesResponse>(
      `/cloudSources/sources/info/${role}/tables`,
      payload,
    ),
    'Failed to fetch tables',
  );

// --- Data Graph Audience Source APIs ---

export const listDataGraphAudienceSources = () =>
  apiAuthCaller()
    .get<DataGraphAudienceSourceResponse[]>('/retl-sources', {
      params: { sourceType: 'data-graph-audience' },
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to list audience sources');
    });

export const createDataGraphAudienceSource = (payload: CreateDataGraphAudienceSourcePayload) =>
  apiAuthCaller()
    .post<DataGraphAudienceSourceResponse>('/retl-sources', payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to create audience source');
    });

export const updateDataGraphAudienceSource = (
  id: string,
  payload: UpdateDataGraphAudienceSourcePayload,
) =>
  apiAuthCaller()
    .put<DataGraphAudienceSourceResponse>(`/retl-sources/${id}`, payload)
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to update audience source');
    });

// --- Data Graph BFF APIs ---

export const getModelProperties = (
  dataGraphId: string,
  modelId: string,
  includeNeighbors = false,
) =>
  apiAuthCaller()
    .get<EnrichedProperty[]>(`/data-graphs/${dataGraphId}/models/${modelId}/properties`, {
      params: { includeNeighbors: String(includeNeighbors) },
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch model properties');
    });

export const getExpressionContext = (dataGraphId: string, modelId: string, expression?: unknown) =>
  apiAuthCaller()
    .post<ExpressionContext>(`/data-graphs/${dataGraphId}/models/${modelId}/expression-context`, {
      expression,
    })
    .catch((e) => {
      throw getApiErrorMessage(e, 'Failed to fetch expression context');
    });

// --- Workspace Token APIs ---

export const listWorkspaceTokensApi = () =>
  apiAuthCaller()
    .get<{ tokens: WorkspaceTokenListItem[] }>('tokens')
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to list workspace tokens'));
    });

export const createWorkspaceTokenApi = (payload: WorkspaceTokenCreateRequest) =>
  apiAuthCaller()
    .post<WorkspaceTokenCreateResponse>('tokens', payload)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to create workspace token'));
    });

export const deleteWorkspaceTokenApi = (tokenId: string) =>
  apiAuthCaller()
    .delete<void>(`tokens/${tokenId}`)
    .catch((e) => {
      throw new Error(getApiErrorMessage(e, 'Failed to delete workspace token'));
    });
